import React, { useState, useMemo, useCallback } from "react";
import { useAppSelector, useAppDispatch } from "../../../../store/hooks";
import { formatNumber } from "../../../../utils/formatting";
import { Sensors, SensorType } from "../../../../types/sensors";
import { SENSORS, calculateSensorCost, calculateSensorTons } from "../../../../constants/sensors";
import { setSensors } from "../../../../store/features/shipBuilderSlice";
import { setSectionSensors } from "../../../../store/features/breakawayHullSlice";
import { formatCurrency } from "../../../../utils/formatting";
import {
  calculateSensorsTons,
} from "../../../../utils/calculations";

interface BreakawaySensorsConfigProps {
  isOpen: boolean;
  onClose: () => void;
  inline?: boolean; // If true, render inline instead of as modal
}

interface SectionSensors {
  sectionId: string;
  type: SensorType;
  name: string;
  description: string;
  capabilities: string[];
  dm: number;
  power: number;
  tons: number;
  cost: number;
  techLevel: number;
  rating: number;
  isAdequate: boolean;
}

export const BreakawaySensorsConfig: React.FC<BreakawaySensorsConfigProps> = ({
  isOpen,
  onClose,
  inline = false,
}) => {
  const dispatch = useAppDispatch();
  const breakawayHull = useAppSelector((state) => state.breakawayHull);
  // const techLevel = useAppSelector((state) => state.shipBuilder.techLevel); // Available for future use
  // const currentSensors = useAppSelector((state) => state.shipBuilder.sensors); // Available for future use
  
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Get section sensors from Redux state (like main SensorsSelector)
  const sectionSensors: Record<string, SectionSensors> = useMemo(() => {
    const sensors: Record<string, SectionSensors> = {};
    if (breakawayHull?.sections) {
      breakawayHull.sections.forEach(section => {
        const sectionSensor = section.sensors;
        if (sectionSensor) {
          sensors[section.id] = {
            sectionId: section.id,
            type: sectionSensor.type as SensorType,
            name: sectionSensor.name,
            description: sectionSensor.description,
            capabilities: sectionSensor.capabilities || [],
            dm: sectionSensor.dm,
            power: sectionSensor.power,
            tons: sectionSensor.tons,
            cost: sectionSensor.cost,
            techLevel: sectionSensor.techLevel,
            rating: sectionSensor.rating,
            isAdequate: true, // All sensor types are adequate
          };
        } else {
          // Default to basic sensors if none configured
          const sensorConfig = SENSORS.BASIC;
          sensors[section.id] = {
            sectionId: section.id,
            type: 'BASIC',
            name: sensorConfig.name || "Basic Sensors",
            description: sensorConfig.description || "Standard sensor suite with Lidar and Radar",
            capabilities: sensorConfig.capabilities ? [...sensorConfig.capabilities] : [],
            dm: sensorConfig.dm || -4,
            power: sensorConfig.power || 0,
            tons: calculateSensorsTons('BASIC') || 0,
            cost: calculateSensorCost('BASIC', section.size),
            techLevel: sensorConfig.techLevel || 8,
            rating: 1,
            isAdequate: true,
          };
        }
      });
    }
    return sensors;
  }, [breakawayHull?.sections]);


  const handleSensorChange = (sectionId: string, sensorType: SensorType) => {
    if (!breakawayHull?.sections) return;

    const section = breakawayHull.sections.find(s => s.id === sectionId);
    if (!section) return;

    const sensorConfig = SENSORS[sensorType];

    // Use the proper calculation functions that handle scaling correctly
    const scaledCost = calculateSensorCost(sensorType, section.size);
    const scaledTons = calculateSensorTons(sensorType, section.size);

    // Immediately update Redux state (like main SensorsSelector)
    const newSensors: Sensors = {
      type: sensorType,
      name: sensorConfig.name || "Unknown Sensors",
      description: sensorConfig.description || "No description available",
      capabilities: sensorConfig.capabilities ? [...sensorConfig.capabilities] : [],
      dm: sensorConfig.dm || 0,
      power: sensorConfig.power || 0,
      tons: scaledTons,
      cost: scaledCost,
      techLevel: sensorConfig.techLevel || 8,
      rating: 1,
    };

    dispatch(setSectionSensors({ sectionId, sensors: newSensors }));

    // Clear error for this section
    setErrors(prev => ({ ...prev, [sectionId]: '' }));
  };

  const handleProportionalDistribution = () => {
    if (!breakawayHull?.sections) return;

    breakawayHull.sections.forEach(section => {
      const sensorType: SensorType = "BASIC"; // Default to basic sensors
      const sensorConfig = SENSORS[sensorType];

      // Use the proper calculation functions that handle scaling correctly
      const scaledCost = calculateSensorCost(sensorType, section.size);
      const scaledTons = calculateSensorTons(sensorType, section.size);

      const newSensors: Sensors = {
        type: sensorType,
        name: sensorConfig.name || "Basic Sensors",
        description: sensorConfig.description || "Standard sensor suite with Lidar and Radar",
        capabilities: sensorConfig.capabilities ? [...sensorConfig.capabilities] : [],
        dm: sensorConfig.dm || -4,
        power: sensorConfig.power || 0,
        tons: scaledTons,
        cost: scaledCost,
        techLevel: sensorConfig.techLevel || 8,
        rating: 1,
      };

      dispatch(setSectionSensors({ sectionId: section.id, sensors: newSensors }));
    });

    setErrors({});
  };

  const getTotalSensorCost = useCallback(() => {
    return Object.values(sectionSensors).reduce((sum, sensors) => sum + sensors.cost, 0);
  }, [sectionSensors]);

  const getTotalSensorWeight = useCallback(() => {
    return Object.values(sectionSensors).reduce((sum, sensors) => sum + sensors.tons, 0);
  }, [sectionSensors]);

  const getTotalSensorPower = useCallback(() => {
    return Object.values(sectionSensors).reduce((sum, sensors) => sum + sensors.power, 0);
  }, [sectionSensors]);

  const getTotalSensorDM = useCallback(() => {
    return Object.values(sectionSensors).reduce((sum, sensors) => sum + sensors.dm, 0);
  }, [sectionSensors]);

  const handleSave = useCallback(() => {
    // Check for errors
    const hasErrors = Object.values(errors).some(error => error !== '');
    if (hasErrors) {
      return;
    }

    // Find the most common sensor type, or use the first one
    const sensorTypes = Object.values(sectionSensors)
      .map(sensors => sensors.type)
      .filter(Boolean) as SensorType[];
    
    const mostCommonType = sensorTypes.length > 0 
      ? sensorTypes.reduce((a, b) => 
          sensorTypes.filter(v => v === a).length >= sensorTypes.filter(v => v === b).length ? a : b
        )
      : 'BASIC' as SensorType;

    // Combine all capabilities
    const allCapabilities = Object.values(sectionSensors)
      .flatMap(sensors => sensors.capabilities)
      .filter((capability, index, array) => array.indexOf(capability) === index); // Remove duplicates

    const sensorConfig = SENSORS[mostCommonType];
    const combinedSensors: Sensors = {
      type: mostCommonType,
      name: sensorConfig.name || "Combined Sensors",
      description: sensorConfig.description || "Combined sensor suite",
      capabilities: allCapabilities,
      dm: getTotalSensorDM(),
      power: getTotalSensorPower(),
      tons: getTotalSensorWeight(),
      cost: getTotalSensorCost(),
      techLevel: sensorConfig.techLevel || 8,
      rating: 1,
    };

    dispatch(setSensors(combinedSensors));

    if (!inline) {
      onClose();
    }
  }, [inline, onClose, errors, sectionSensors, dispatch, getTotalSensorCost, getTotalSensorDM, getTotalSensorPower, getTotalSensorWeight]);


  if (!isOpen || !breakawayHull?.sections) return null;

  const totalSensorCost = getTotalSensorCost();
  const totalSensorWeight = getTotalSensorWeight();
  const totalSensorPower = getTotalSensorPower();
  const totalSensorDM = getTotalSensorDM();

  const content = (
    <div className={inline ? "w-full" : "bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"}>
      {!inline && (
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Breakaway Sensors Configuration
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <div className="mb-4">
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-3 mb-4">
          <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">Breakaway Sensor Rules</h3>
          <ul className="text-xs text-blue-800 dark:text-blue-300 space-y-1">
            <li>• Each breakaway section can have its own sensor system for independent operation</li>
            <li>• When sections are together, sensors can share coverage and capabilities</li>
            <li>• Sensor capabilities are combined for the main ship</li>
            <li>• Each section's sensors must meet that section's operational requirements</li>
            <li>• Combined sensor statistics are calculated for the main ship</li>
          </ul>
        </div>
      </div>

      {/* Proportional Distribution Button */}
      <div className="mb-4">
        <button
          onClick={handleProportionalDistribution}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          Distribute Breakaway Sensors by Section
        </button>
      </div>

      {/* Section Sensor Configuration */}
      <div className="space-y-6">
        {breakawayHull.sections.map((section) => {
          const sectionSensor = sectionSensors[section.id] || {
            sectionId: section.id,
            type: 'BREAKAWAY' as SensorType,
            name: "Breakaway Hull Sensors",
            description: "Specialized sensor suite for breakaway hull sections",
            capabilities: [],
            dm: -1,
            power: 7,
            tons: 1,
            cost: 1500000,
            techLevel: 9,
            rating: 1,
            isAdequate: true,
          };
          const error = errors[section.id];

          return (
            <div key={section.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                {section.name} ({section.size} tons)
              </h4>

              {error && (
                <div className="text-red-600 dark:text-red-400 text-sm mb-3 p-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Sensor Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sensor Type
                  </label>
                  <select
                    value={sectionSensor.type}
                    onChange={(e) => handleSensorChange(section.id, e.target.value as SensorType)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                  >
                    {Object.entries(SENSORS).map(([key, config]) => (
                      <option key={key} value={key}>
                        {config.name} (Min TL {config.techLevel})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sensor Weight */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sensor Weight
                  </label>
                  <div className="text-sm text-gray-900 dark:text-gray-100 font-medium">
                    {formatNumber(sectionSensor.tons)} tons
                  </div>
                </div>
              </div>

              {/* Sensor Details */}
              <div className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                <div className="mb-2">
                  <strong>Sensor Details:</strong>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>Cost: {formatCurrency(sectionSensor.cost)}</div>
                  <div>Power: {formatNumber(sectionSensor.power)} Power</div>
                  <div>DM: {sectionSensor.dm > 0 ? "+" : ""}{sectionSensor.dm}</div>
                  <div>Tech Level: {sectionSensor.techLevel}</div>
                </div>
              </div>

              {/* Adequacy Status */}
              <div className={`mt-2 text-sm font-medium ${sectionSensor.isAdequate ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {sectionSensor.isAdequate ? '✓ Sensors adequate for section requirements' : '✗ Sensors inadequate for section requirements'}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="mt-6 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Combined Sensor Summary</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600 dark:text-gray-300">Total Sensor Weight:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{formatNumber(totalSensorWeight)} tons</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-300">Total Sensor Cost:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{formatCurrency(totalSensorCost)}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-300">Total Sensor Power:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{formatNumber(totalSensorPower)} Power</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-300">Total Sensor DM:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">{totalSensorDM > 0 ? "+" : ""}{totalSensorDM}</span>
          </div>
        </div>
      </div>

      {/* General Errors */}
      {errors.general && (
        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-md">
          <p className="text-sm text-red-700 dark:text-red-300">{errors.general}</p>
        </div>
      )}

      {!inline && (
        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 mr-3"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={Object.values(errors).some(error => error !== '')}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Configuration
          </button>
        </div>
      )}
    </div>
  );

  if (inline) {
    return content;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      {content}
    </div>
  );
}; 